import {
  GridColDef,
  GridColumnGroupingModel,
  gridPageSelector,
  gridPageSizeSelector,
  GridPaginationModel,
  GridRenderCellParams,
  gridRowCountSelector,
  GridSortModel,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {
  isAssignableTo,
  maybe,
  Type,
  typedDict,
  typedDictPropertyTypes,
} from '@wandb/weave/core';
import {useDeepMemo} from '@wandb/weave/hookUtils';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {CellValue} from '../../../../Browse2/CellValue';
import {flattenObjectPreservingWeaveTypes} from '../../../flattenObject';
import {StyledDataGrid} from '../../../StyledDataGrid';
import {generateStableDigest} from '../../common/digestUtils';
import {isPredictAndScoreOpRef} from '../../common/heuristics';
import {useWFHooks} from '../../wfReactInterface/context';
import {Query} from '../../wfReactInterface/traceServerClientInterface/query';
import {
  SortBy,
  TraceCallSchema,
} from '../../wfReactInterface/traceServerClientTypes';
import {useEvalTabData} from './useEvalTabData';

type DatasetPreviewGridProps = {
  evaluationEntity: string;
  evaluationProject: string;
  evaluationCallId: string;
  pageSize?: number;
};

type PageSizeOption = {
  value: number;
  label: string;
};

const CustomPaginationFooter: React.FC = () => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);

  const handlePrevPage = () => {
    apiRef.current.setPage(page - 1);
  };

  const handleNextPage = () => {
    apiRef.current.setPage(page + 1);
  };

  // Calculate the item range being displayed
  const start = rowCount > 0 ? page * pageSize + 1 : 0;
  const end = Math.min(rowCount, (page + 1) * pageSize);

  const pageSizes = [5, 10, 20, 50];
  if (!pageSizes.includes(pageSize)) {
    pageSizes.push(pageSize);
    pageSizes.sort((a, b) => a - b);
  }
  const pageSizeOptions = pageSizes.map(sz => ({
    value: sz,
    label: sz.toString(),
  }));

  // Create a custom display value showing the current range
  const rangeDisplayValue = {
    value: pageSize,
    label: `${start}-${end}`,
  };

  const onPageSizeChange = (option: PageSizeOption | null) => {
    if (option) {
      apiRef.current.setPageSize(option.value);
    }
  };

  const pageCount = Math.ceil(rowCount / pageSize);

  return (
    <Tailwind className="w-full">
      <div className="flex w-full items-center justify-end p-8 border-t border-moon-200">
        <div className="flex items-center gap-8 mr-8">
          <Select<PageSizeOption>
            menuPlacement="top"
            size="small"
            options={pageSizeOptions}
            value={rangeDisplayValue}
            isSearchable={false}
            onChange={onPageSizeChange}
          />
          <span className="text-sm font-normal text-moon-500 dark:text-moon-350">
            of {rowCount}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="small"
            onClick={handlePrevPage}
            disabled={page === 0}
            icon="chevron-back"
          />
          <Button
            variant="ghost"
            size="small"
            onClick={handleNextPage}
            disabled={page >= pageCount - 1}
            icon="chevron-next"
          />
        </div>
      </div>
    </Tailwind>
  );
};

export const DatasetPreviewGrid: React.FC<DatasetPreviewGridProps> = ({
  evaluationEntity,
  evaluationProject,
  evaluationCallId,
  pageSize = 10,
}) => {
  const {useCalls, useRefsData, useCallsStats} = useWFHooks();
  const {setPredictAndScoreOpVersionRef} = useEvalTabData();

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize,
  });
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 800
  );

  const onPaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model);
  }, []);

  useEffect(() => {
    setPaginationModel(prev => {
      if (prev.pageSize === pageSize) {
        return prev;
      }
      return {
        ...prev,
        page: 0,
        pageSize,
      };
    });
  }, [pageSize]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const limit = paginationModel.pageSize;
  const offset = paginationModel.page * paginationModel.pageSize;
  const sortBy = useMemo<SortBy[]>(() => {
    return sortModel.map(sort => ({
      field: mapGridFieldToCallField(sort.field),
      direction: sort.sort === 'asc' ? 'asc' : 'desc',
    }));
  }, [sortModel]);

  const excludeSummarizeQuery: Query = useMemo(
    () => ({
      $expr: {
        $not: [
          {
            $contains: {
              input: {$getField: 'op_name'},
              substr: {$literal: 'Evaluation.summarize:'},
            },
          },
        ],
      },
    }),
    []
  );

  const callsStats = useCallsStats({
    entity: evaluationEntity,
    project: evaluationProject,
    filter: {
      parentIds: [evaluationCallId],
    },
    query: excludeSummarizeQuery,
    skip: false,
  });

  const predictAndScoreCalls = useCalls({
    entity: evaluationEntity,
    project: evaluationProject,
    filter: {
      parentIds: [evaluationCallId],
    },
    query: excludeSummarizeQuery,
    limit,
    offset,
    sortBy,
    columns: [
      'id',
      'inputs',
      'output',
      'attributes',
      'parent_id',
      'summary',
      'started_at',
      'ended_at',
      'op_name',
    ],
    includeFeedback: true,
    skip: false,
  });

  const predictAndScoreOpVersionRef = useMemo(() => {
    if (!predictAndScoreCalls.result) {
      return null;
    }

    const calls = predictAndScoreCalls.result;
    const predictAndScoreCall =
      calls.find(call => {
        const opRef = call.opVersionRef;
        if (opRef == null) {
          return false;
        }
        return isPredictAndScoreOpRef(opRef);
      }) ?? null;

    return predictAndScoreCall?.opVersionRef ?? null;
  }, [predictAndScoreCalls.result]);

  useEffect(() => {
    setPredictAndScoreOpVersionRef(predictAndScoreOpVersionRef);
  }, [predictAndScoreOpVersionRef, setPredictAndScoreOpVersionRef]);

  // First pass: Collect dataset row references from calls
  const filteredCallsAndRowRefs = useMemo(() => {
    const calls: Array<{
      traceCall: TraceCallSchema;
      rowRef: string | null;
    }> = [];
    const rowRefs: string[] = [];

    if (predictAndScoreCalls.result) {
      predictAndScoreCalls.result.forEach(call => {
        const traceCall = call.traceCall;
        const exampleInput = traceCall.inputs?.example;

        // Check once if this is a dataset row reference
        const rowRef =
          typeof exampleInput === 'string' &&
          exampleInput.includes('/attr/rows/id/')
            ? exampleInput
            : null;

        calls.push({traceCall, rowRef});

        if (rowRef) {
          rowRefs.push(rowRef);
        }
      });
    }

    return {calls, rowRefs};
  }, [predictAndScoreCalls.result]);

  const datasetRowsData = useRefsData({
    refUris: filteredCallsAndRowRefs.rowRefs,
  });

  // Second pass: Build final data structure with fetched dataset rows
  const dataAsListOfDict = useMemo(() => {
    const rowDataMap = new Map<string, any>();
    if (datasetRowsData.result && filteredCallsAndRowRefs.rowRefs.length > 0) {
      filteredCallsAndRowRefs.rowRefs.forEach((rowRef, index) => {
        if (
          datasetRowsData.result != null &&
          datasetRowsData.result[index] != null
        ) {
          rowDataMap.set(rowRef, datasetRowsData.result[index]);
        }
      });
    }

    return filteredCallsAndRowRefs.calls.map(({traceCall, rowRef}) => {
      const combined: {[key: string]: any} = {};
      const exampleInput = traceCall.inputs?.example;

      // Compute digest
      let digest: string | undefined;
      if (typeof exampleInput === 'object' && exampleInput !== null) {
        digest = generateStableDigest(exampleInput);
      } else if (typeof exampleInput === 'string') {
        const rowDigest = extractRowDigestFromExampleRef(exampleInput);
        if (rowDigest) {
          digest = rowDigest;
        } else {
          digest = generateStableDigest(exampleInput);
        }
      }

      // Process inputs
      if (rowRef) {
        const rowData = rowDataMap.get(rowRef);
        if (rowData != null) {
          const inputData = flattenWeaveValue(rowData);
          if (Object.keys(inputData).length > 0) {
            Object.assign(combined, prefixFlattened(inputData, 'inputs'));
          }
        }
      } else if (exampleInput != null) {
        const inputData = flattenWeaveValue(exampleInput);
        if (Object.keys(inputData).length > 0) {
          Object.assign(combined, prefixFlattened(inputData, 'inputs'));
        }
      }

      // Process outputs
      const outputData = buildOutputData(traceCall);
      if (Object.keys(outputData).length > 0) {
        Object.assign(combined, prefixFlattened(outputData, 'outputs'));
      }

      // Process scores
      const scoreData = buildScoreData(traceCall);
      if (Object.keys(scoreData).length > 0) {
        Object.assign(combined, prefixFlattened(scoreData, 'scores'));
      }

      return {
        digest: digest ?? `call-${traceCall.id}`,
        data: combined,
      };
    });
  }, [filteredCallsAndRowRefs, datasetRowsData.result]);

  // Combined loading state - wait for all data to be ready
  const allDataLoaded = useMemo(() => {
    if (predictAndScoreCalls.loading) {
      return false;
    }
    if (filteredCallsAndRowRefs.rowRefs.length > 0 && datasetRowsData.loading) {
      return false;
    }
    return true;
  }, [
    predictAndScoreCalls.loading,
    filteredCallsAndRowRefs.rowRefs.length,
    datasetRowsData.loading,
  ]);

  type GridRow = {
    id: string | number;
    digest?: string;
    [key: string]: any;
  };

  const gridRows = useMemo<GridRow[]>(() => {
    return dataAsListOfDict.map(({digest, data}) => ({
      id: digest,
      digest,
      ...data,
    }));
  }, [dataAsListOfDict]);

  const objectType = useMemo(() => {
    if (dataAsListOfDict == null || dataAsListOfDict.length === 0) {
      return typedDict({});
    }

    const propertyTypes: {[col: string]: Type} = {};
    dataAsListOfDict.forEach(({data}) => {
      Object.keys(data).forEach(col => {
        if (propertyTypes[col] == null) {
          if (data[col] == null) {
          } else if (typeof data[col] === 'boolean') {
            propertyTypes[col] = 'boolean';
          } else if (typeof data[col] === 'string') {
            propertyTypes[col] = 'string';
          } else if (typeof data[col] === 'number') {
            propertyTypes[col] = 'number';
          } else if (Array.isArray(data[col])) {
            propertyTypes[col] = {type: 'list', objectType: 'any'};
          } else {
            propertyTypes[col] = 'any';
          }
        }
      });
    });
    return typedDict(propertyTypes);
  }, [dataAsListOfDict]);

  const objectTypeDeepMemo = useDeepMemo(objectType);

  const columnSpec: GridColDef[] = useMemo(() => {
    const maxWidth = windowWidth * 0.75;
    const minWidth = 100;
    const propertyTypes = typedDictPropertyTypes(objectTypeDeepMemo);

    const columns: GridColDef[] = [];
    const sortedPropertyEntries = Object.entries(propertyTypes).sort(
      ([keyA], [keyB]) => {
        const orderA = getColumnGroupOrder(keyA);
        const orderB = getColumnGroupOrder(keyB);
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return keyA.localeCompare(keyB);
      }
    );

    sortedPropertyEntries.forEach(([key, valueType]) => {
      const sourceKey = key === '' ? 'value' : key;
      const headerLabel = formatHeaderLabel(sourceKey);

      let colType: GridColDef['type'] = 'string';
      if (isAssignableTo(valueType, maybe('boolean'))) {
        colType = 'boolean';
      } else if (isAssignableTo(valueType, maybe('number'))) {
        colType = 'number';
      } else if (
        isAssignableTo(valueType, maybe({type: 'list', objectType: 'any'}))
      ) {
        colType = 'string';
      }

      // Disable sorting for input columns (dataset values)
      // Enable sorting for output and score columns
      // TODO: Enable sorting for dataset columns. This will involve
      // detecting whether the inputs are coming from a dataset row or
      // linlined in the call inputs. If they are from a dataset row, we
      // need to expand the inputs.example field in the query to be able
      // to sort by the dataset row values.
      const isSortable = !sourceKey.startsWith('inputs.');

      columns.push({
        maxWidth,
        minWidth,
        flex: 1,
        display: 'flex',
        type: colType,
        editable: false,
        sortable: isSortable,
        field: sourceKey,
        headerName: headerLabel,
        renderHeader: () => headerLabel,
        renderCell: (params: GridRenderCellParams) => {
          const data = params.row[sourceKey];
          return <CellValue value={data} field={params.field} />;
        },
      });
    });
    return columns;
  }, [objectTypeDeepMemo, windowWidth]);

  const columnGroupingModel: GridColumnGroupingModel = useMemo(() => {
    const inputColumns = columnSpec.filter(col =>
      col.field.startsWith('inputs.')
    );
    const outputColumns = columnSpec.filter(col =>
      col.field.startsWith('outputs.')
    );
    const scoreColumns = columnSpec.filter(col =>
      col.field.startsWith('scores.')
    );

    const groups: GridColumnGroupingModel = [];

    if (inputColumns.length > 0) {
      const inputFields = inputColumns.map(c => c.field);
      groups.push({
        groupId: 'inputs',
        headerName: 'Dataset',
        children: buildNestedGroups(inputFields, 1),
        renderHeaderGroup: () => 'Dataset',
      });
    }

    if (outputColumns.length > 0) {
      const outputFields = outputColumns.map(c => c.field);
      groups.push({
        groupId: 'outputs',
        headerName: 'Output',
        children: buildNestedGroups(outputFields, 1),
        renderHeaderGroup: () => 'Output',
      });
    }

    if (scoreColumns.length > 0) {
      const scoreFields = scoreColumns.map(c => c.field);
      groups.push({
        groupId: 'scores',
        headerName: 'Scores',
        children: buildNestedGroups(scoreFields, 1),
        renderHeaderGroup: () => 'Scores',
      });
    }

    return groups;
  }, [columnSpec]);

  const headerHeight = 84;
  const footerHeight = 41;
  const rowHeight = 36;
  const tableHeight = headerHeight + footerHeight + rowHeight * 10;

  // Show loading state until all data is ready
  if (!allDataLoaded) {
    return (
      <div
        className="flex items-center justify-center w-full"
        style={{height: tableHeight}}>
        <WaveLoader size="small" delayBeforeShow={0} />
      </div>
    );
  }

  // Show empty state when there are no predict/score calls
  if (gridRows.length === 0) {
    return (
      <div
        className="flex items-center justify-center w-full"
        style={{height: tableHeight}}>
        <div className="text-sm text-moon-500">
          No evaluation results found.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" style={{height: tableHeight}}>
      <StyledDataGrid
        disableColumnFilter
        disableMultipleColumnsFiltering
        disableColumnPinning={false}
        disableColumnReorder={false}
        disableColumnResize={false}
        disableColumnSelector
        disableMultipleColumnsSorting
        keepBorders={false}
        density="compact"
        columnHeaderHeight={40}
        rows={gridRows}
        columns={columnSpec}
        columnGroupingModel={columnGroupingModel}
        loading={false}
        disableRowSelectionOnClick
        slots={{
          baseTooltip: () => null,
          footer: CustomPaginationFooter,
        }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={callsStats.result?.count ?? 0}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
      />
    </div>
  );
};

const buildNestedGroups = (
  fields: string[],
  depth: number
): Array<{field: string} | any> => {
  if (fields.length === 0) return [];

  const grouped = new Map<string, string[]>();
  const leafFields: string[] = [];

  fields.forEach(field => {
    const parts = field.split('.');
    if (parts.length > depth + 1) {
      const key = parts[depth];
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(field);
    } else {
      leafFields.push(field);
    }
  });

  const result: any[] = [];

  leafFields.forEach(field => {
    result.push({field});
  });

  grouped.forEach((groupFields, groupName) => {
    const groupId = groupFields[0]
      .split('.')
      .slice(0, depth + 1)
      .join('.');
    result.push({
      groupId,
      headerName: groupName,
      children: buildNestedGroups(groupFields, depth + 1),
      renderHeaderGroup: () => groupName,
    });
  });

  return result;
};

const flattenWeaveValue = (value: any): {[key: string]: any} => {
  if (value == null) {
    return {};
  }
  if (Array.isArray(value)) {
    return {value};
  }
  if (typeof value === 'object') {
    const {flattened} = flattenObjectPreservingWeaveTypes(value);
    return flattened;
  }
  return {value};
};

const prefixFlattened = (
  data: {[key: string]: any},
  prefix: string
): {[key: string]: any} => {
  return Object.fromEntries(
    Object.entries(data).map(([key, val]) => [`${prefix}.${key}`, val])
  );
};

const buildOutputData = (call: TraceCallSchema) => {
  const maybeOutput = (call.output as any)?.output ?? call.output;
  if (maybeOutput != null) {
    return flattenWeaveValue(maybeOutput);
  }
  return {};
};

const buildScoreData = (call: TraceCallSchema) => {
  const scores = (call.output as any)?.scores;
  if (scores != null) {
    return flattenWeaveValue(scores);
  }
  return {};
};

const extractRowDigestFromExampleRef = (exampleRef: any): string | null => {
  const split = '/attr/rows/id/';
  if (typeof exampleRef === 'string' && exampleRef.includes(split)) {
    const parts = exampleRef.split(split);
    if (parts.length === 2) {
      const maybeDigest = parts[1];
      if (maybeDigest != null && !maybeDigest.includes('/')) {
        return maybeDigest;
      }
    }
  }
  return null;
};

const formatPathLabel = (path: string): string => {
  if (!path) {
    return 'value';
  }
  return path.split('.').filter(Boolean).join(' · ');
};

const formatHeaderLabel = (key: string): string => {
  // For any nested field, just show the last component since all parent
  // components will be rendered as group headers
  const parts = key.split('.');
  if (parts.length > 2) {
    // Return only the last component
    return formatPathLabel(parts[parts.length - 1]);
  }

  // For non-nested fields, strip the prefix
  if (key.startsWith('inputs.')) {
    return formatPathLabel(key.slice('inputs.'.length));
  }
  if (key.startsWith('outputs.')) {
    return formatPathLabel(key.slice('outputs.'.length));
  }
  if (key.startsWith('scores.')) {
    return formatPathLabel(key.slice('scores.'.length));
  }
  return formatPathLabel(key);
};

const getColumnGroupOrder = (key: string): number => {
  if (key.startsWith('inputs.')) {
    return 0;
  }
  if (key.startsWith('outputs.')) {
    return 1;
  }
  if (key.startsWith('scores.')) {
    return 2;
  }
  return 3;
};

const mapGridFieldToCallField = (field: string): string => {
  // Map grid column names to actual call structure for backend sorting
  // For predict_and_score calls:
  // outputs.X -> output.output.X (the actual output is nested at output.output)
  // scores.X -> output.scores.X (scores are at output.scores)
  if (field.startsWith('outputs.')) {
    return `output.output.${field.slice('outputs.'.length)}`;
  }
  if (field.startsWith('scores.')) {
    return `output.${field}`;
  }
  return field;
};
